/* ProposalBuilderPage — Milestone 2: create/edit a draft proposal.
 * Handles /proposals/new?org=<id> and /proposals/:id.
 * Line items are entered in dollars; totals preview is computed client-side for
 * UX but the SERVER recomputes authoritative totals on save. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  FileSignature, Plus, Trash2, Loader2, ArrowLeft, Save, Upload, Send, Copy, Sparkles, FileDown, LayoutTemplate, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { ProposalDocument } from '../components/ProposalDocument';
import { exportNodeToPdf } from '../lib/proposalPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);
const toDollars = (c) => ((c || 0) / 100).toFixed(2);
const money = (cents, currency = 'usd') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100); }
  catch { return `$${((cents || 0) / 100).toFixed(2)}`; }
};

const blankLine = () => ({
  id: crypto.randomUUID(),
  name: '', description: '',
  qty: 1, unit_price: '0.00', discount: '0.00', tax_pct: 0,
  billing: 'one_time',
});

const ProposalBuilderPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';

  const [orgId, setOrgId] = useState(sp.get('org') || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sending, setSending] = useState(false);
  const [publicToken, setPublicToken] = useState(null);
  const [events, setEvents] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const docRef = useRef(null);
  const [form, setForm] = useState({
    title: 'Untitled Proposal',
    currency: 'usd',
    prospect: { name: '', email: '', company: '' },
    branding: { company_name: '', color: '#6366f1', logo_url: '', company_info: '' },
    intro: '', scope: '', terms: '',
    payment_option: 'immediate', deposit_pct: 50,
    line_items: [blankLine()],
    status: 'draft',
    number: null,
  });

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await api.get(`/proposals/${id}`);
      const p = r.data;
      setOrgId(p.organization_id);
      setForm({
        title: p.title || 'Untitled Proposal',
        currency: p.currency || 'usd',
        prospect: { name: '', email: '', company: '', ...(p.prospect || {}) },
        branding: { company_name: '', color: '#6366f1', logo_url: '', company_info: '', ...(p.branding || {}) },
        intro: p.intro || '', scope: p.scope || '', terms: p.terms || '',
        payment_option: p.payment_option || 'immediate',
        deposit_pct: p.deposit_pct ?? 50,
        line_items: (p.line_items || []).map((li) => ({
          id: li.id || crypto.randomUUID(),
          name: li.name || '', description: li.description || '',
          qty: li.qty ?? 1,
          unit_price: toDollars(li.unit_price_cents),
          discount: toDollars(li.discount_cents),
          tax_pct: li.tax_pct ?? 0,
          billing: li.billing || 'one_time',
        })),
        status: p.status,
        number: p.number,
      });
      setPublicToken(p.public_token || null);
      if (p.status !== 'draft') {
        try { const ev = await api.get(`/proposals/${id}/events`); setEvents(ev.data.events || []); } catch { /* noop */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load proposal'); navigate('/proposals'); }
    finally { setLoading(false); }
  }, [api, id, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  // When opened directly (no ?org=), default to the user's first organization.
  useEffect(() => {
    if (isNew && !orgId) {
      api.get('/orgs').then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data.organizations || r.data.orgs || []);
        if (list.length) setOrgId(list[0].id);
      }).catch(() => {});
    }
  }, [isNew, orgId, api]);

  // Pre-fill a new proposal from a template (?template=<id>).
  useEffect(() => {
    const tid = sp.get('template');
    if (!isNew || !tid) return;
    api.get(`/proposal-templates/${tid}`).then(({ data }) => {
      setForm((f) => ({
        ...f,
        currency: data.currency || f.currency,
        branding: { ...f.branding, ...(data.branding || {}) },
        intro: data.intro || '', scope: data.scope || '', terms: data.terms || '',
        payment_option: data.payment_option || 'immediate',
        deposit_pct: data.deposit_pct ?? 50,
        line_items: (data.line_items || []).map((li) => ({
          id: crypto.randomUUID(),
          name: li.name || '', description: li.description || '',
          qty: li.qty ?? 1,
          unit_price: toDollars(li.unit_price_cents),
          discount: toDollars(li.discount_cents),
          tax_pct: li.tax_pct ?? 0,
          billing: li.billing || 'one_time',
        })),
      }));
      toast.success('Loaded from template');
    }).catch(() => {});
  }, [isNew, sp, api]);

  // Live client-side totals preview
  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, tax = 0, oneTime = 0, monthly = 0;
    for (const li of form.line_items) {
      const gross = Math.round((parseFloat(li.qty) || 0) * toCents(li.unit_price));
      const disc = toCents(li.discount);
      const net = Math.max(0, gross - disc);
      const t = Math.round((net * (parseFloat(li.tax_pct) || 0)) / 100);
      subtotal += gross; discount += disc; tax += t;
      if (li.billing === 'monthly') monthly += net + t; else oneTime += net + t;
    }
    return { subtotal, discount, tax, oneTime, monthly };
  }, [form.line_items]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (group, k, v) => setForm((f) => ({ ...f, [group]: { ...f[group], [k]: v } }));
  const setLine = (idx, k, v) => setForm((f) => ({
    ...f, line_items: f.line_items.map((li, i) => (i === idx ? { ...li, [k]: v } : li)),
  }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, blankLine()] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const buildPayload = () => ({
    title: form.title,
    currency: form.currency,
    prospect: form.prospect,
    branding: form.branding,
    intro: form.intro, scope: form.scope, terms: form.terms,
    payment_option: form.payment_option,
    deposit_pct: form.payment_option === 'deposit' ? Number(form.deposit_pct) : null,
    line_items: form.line_items.map((li) => ({
      id: li.id,
      name: li.name || 'Item',
      description: li.description || '',
      qty: parseFloat(li.qty) || 0,
      unit_price_cents: toCents(li.unit_price),
      discount_cents: toCents(li.discount),
      tax_pct: parseFloat(li.tax_pct) || 0,
      billing: li.billing,
    })),
  });

  const save = async () => {
    if (!orgId) { toast.error('No organization selected'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post('/proposals', { organization_id: orgId, ...buildPayload() });
        toast.success('Proposal created');
        navigate(`/proposals/${r.data.id}`, { replace: true });
      } else {
        await api.put(`/proposals/${id}`, buildPayload());
        toast.success('Proposal saved');
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save proposal'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNested('branding', 'logo_url', r.data.url);
      toast.success('Logo uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Logo upload failed'); }
    finally { setUploadingLogo(false); }
  };

  const sendProposal = async () => {
    if (isNew) { toast.error('Save the proposal first'); return; }
    if (!form.prospect.email) { toast.error('Add a client email before sending'); return; }
    if (!window.confirm(`Send this proposal to ${form.prospect.email}? The draft will be locked.`)) return;
    setSending(true);
    try {
      if (isDraft) await api.put(`/proposals/${id}`, buildPayload()); // persist latest edits before freezing
      const r = await api.post(`/proposals/${id}/send`);
      toast.success('Proposal sent to client');
      if (r.data.public_url) navigator.clipboard?.writeText(r.data.public_url).catch(() => {});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send proposal'); }
    finally { setSending(false); }
  };

  const copyPublicLink = () => {
    if (!publicToken) return;
    const url = `${window.location.origin}/proposal/${publicToken}`;
    navigator.clipboard?.writeText(url);
    toast.success('Public link copied');
  };

  const runAiDraft = async () => {
    if (!aiBrief.trim()) { toast.error('Describe the project first'); return; }
    setAiBusy(true);
    try {
      const r = await api.post('/proposals/ai-draft', { title: form.title, brief: aiBrief.trim(), currency: form.currency });
      const d = r.data;
      setForm((f) => ({
        ...f,
        intro: d.intro || f.intro,
        scope: d.scope || f.scope,
        terms: d.terms || f.terms,
        line_items: (d.line_items && d.line_items.length)
          ? d.line_items.map((li) => ({
              id: crypto.randomUUID(),
              name: li.name || 'Item', description: li.description || '',
              qty: li.qty ?? 1,
              unit_price: toDollars(li.unit_price_cents),
              discount: '0.00', tax_pct: li.tax_pct ?? 0,
              billing: li.billing || 'one_time',
            }))
          : f.line_items,
      }));
      toast.success('Draft generated — review & edit before saving');
      setAiOpen(false); setAiBrief('');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); }
    finally { setAiBusy(false); }
  };

  const formToSnap = () => ({
    number: form.number || 'DRAFT',
    title: form.title,
    currency: form.currency,
    branding: form.branding,
    prospect: { name: form.prospect.name, company: form.prospect.company },
    intro: form.intro, scope: form.scope, terms: form.terms,
    payment_option: form.payment_option, deposit_pct: form.deposit_pct,
    line_items: form.line_items.map((li) => ({
      id: li.id, name: li.name, description: li.description,
      qty: parseFloat(li.qty) || 0,
      unit_price_cents: toCents(li.unit_price),
      discount_cents: toCents(li.discount),
      tax_pct: parseFloat(li.tax_pct) || 0,
      billing: li.billing,
    })),
    totals: {
      one_time_total_cents: totals.oneTime,
      monthly_total_cents: totals.monthly,
    },
  });

  const downloadPdf = async () => {
    setDownloading(true);
    try { await exportNodeToPdf(docRef.current, `${form.number || 'proposal'}.pdf`); toast.success('PDF saved'); }
    catch { toast.error('Could not generate PDF'); }
    finally { setDownloading(false); }
  };

  const sendFromPreview = async () => {
    await sendProposal();
    setPreviewOpen(false);
  };

  const saveAsTemplate = async () => {
    if (!orgId) { toast.error('No organization selected'); return; }
    const name = window.prompt('Template name', form.title || 'My template');
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      const p = buildPayload();
      await api.post('/proposal-templates', {
        organization_id: orgId, name: name.trim(),
        currency: p.currency, branding: p.branding,
        intro: p.intro, scope: p.scope, terms: p.terms,
        line_items: p.line_items, payment_option: p.payment_option, deposit_pct: p.deposit_pct,
      });
      toast.success('Saved as template');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save template'); }
    finally { setSavingTemplate(false); }
  };

  const isDraft = form.status === 'draft';

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="proposal-builder-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/proposals')} data-testid="back-to-proposals"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-indigo-600" />
              {isNew ? 'New Proposal' : form.number}
            </h1>
            {!isNew && <p className="text-xs text-zinc-500 mt-0.5">Status: {form.status}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-draft-btn">
              <Sparkles className="w-4 h-4 mr-2 text-indigo-600" /> Draft with AI
            </Button>
          )}
          {isDraft && (
            <Button variant="outline" onClick={saveAsTemplate} disabled={savingTemplate} data-testid="save-template-btn">
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LayoutTemplate className="w-4 h-4 mr-2" />} Save as template
            </Button>
          )}
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="preview-pdf-btn">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          {!isNew && !isDraft && (
            <Button variant="outline" onClick={copyPublicLink} data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
          )}
          {!isNew && (form.status === 'sent' || form.status === 'viewed') && (
            <Button variant="outline" onClick={sendProposal} disabled={sending} data-testid="send-proposal-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Resend
            </Button>
          )}
          {isDraft && (
            <Button onClick={save} disabled={saving} data-testid="save-proposal-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isNew ? 'Create draft' : 'Save changes'}
            </Button>
          )}
        </div>
      </div>

      {/* Basics */}
      <Card>
        <CardHeader><CardTitle className="text-base">Proposal details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setField('title', e.target.value)} data-testid="proposal-title-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Client name</Label><Input value={form.prospect.name} onChange={(e) => setNested('prospect', 'name', e.target.value)} data-testid="prospect-name-input" /></div>
            <div><Label>Client email</Label><Input value={form.prospect.email} onChange={(e) => setNested('prospect', 'email', e.target.value)} data-testid="prospect-email-input" /></div>
            <div><Label>Company</Label><Input value={form.prospect.company} onChange={(e) => setNested('prospect', 'company', e.target.value)} data-testid="prospect-company-input" /></div>
          </div>
          <div><Label>Introduction</Label><Textarea rows={2} value={form.intro} onChange={(e) => setField('intro', e.target.value)} data-testid="proposal-intro-input" /></div>
          <div><Label>Scope of work</Label><Textarea rows={3} value={form.scope} onChange={(e) => setField('scope', e.target.value)} data-testid="proposal-scope-input" /></div>
          <div><Label>Terms</Label><Textarea rows={2} value={form.terms} onChange={(e) => setField('terms', e.target.value)} data-testid="proposal-terms-input" /></div>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card>
        <CardHeader><CardTitle className="text-base">Branding</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <div><Label>Your company name</Label><Input value={form.branding.company_name} onChange={(e) => setNested('branding', 'company_name', e.target.value)} data-testid="branding-company-input" /></div>
          <div><Label>Brand color</Label><Input type="color" className="h-10 w-20 p-1" value={form.branding.color} onChange={(e) => setNested('branding', 'color', e.target.value)} data-testid="branding-color-input" /></div>
          <div><Label>Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.branding.logo_url && (
                <img src={fullUrl(form.branding.logo_url)} alt="logo" className="h-10 w-10 rounded object-contain border border-zinc-200 bg-white" data-testid="logo-preview" />
              )}
              <input id="logo-file" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
              <Button type="button" variant="outline" size="sm" disabled={uploadingLogo} onClick={() => document.getElementById('logo-file').click()} data-testid="logo-upload-btn">
                {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {form.branding.logo_url ? 'Replace' : 'Upload logo'}
              </Button>
              {form.branding.logo_url && (
                <Button type="button" variant="ghost" size="sm" className="text-rose-600" onClick={() => setNested('branding', 'logo_url', '')} data-testid="logo-remove-btn">Remove</Button>
              )}
            </div>
          </div>
          <div><Label>Company info</Label><Input value={form.branding.company_info} onChange={(e) => setNested('branding', 'company_info', e.target.value)} data-testid="branding-info-input" /></div>
        </CardContent>
      </Card>

      {/* Line items */}
      <Card>
        <CardHeader><CardTitle className="text-base">Line items</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {form.line_items.map((li, idx) => (
            <div key={li.id} className="grid grid-cols-12 gap-2 items-end border-b border-zinc-100 pb-3" data-testid={`line-item-${idx}`}>
              <div className="col-span-12 sm:col-span-4"><Label className="text-xs">Item</Label><Input value={li.name} onChange={(e) => setLine(idx, 'name', e.target.value)} placeholder="Service name" data-testid={`line-name-${idx}`} /></div>
              <div className="col-span-4 sm:col-span-1"><Label className="text-xs">Qty</Label><Input type="number" min="0" value={li.qty} onChange={(e) => setLine(idx, 'qty', e.target.value)} data-testid={`line-qty-${idx}`} /></div>
              <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Unit ($)</Label><Input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => setLine(idx, 'unit_price', e.target.value)} data-testid={`line-price-${idx}`} /></div>
              <div className="col-span-4 sm:col-span-1"><Label className="text-xs">Disc ($)</Label><Input type="number" min="0" step="0.01" value={li.discount} onChange={(e) => setLine(idx, 'discount', e.target.value)} data-testid={`line-discount-${idx}`} /></div>
              <div className="col-span-4 sm:col-span-1"><Label className="text-xs">Tax %</Label><Input type="number" min="0" max="100" value={li.tax_pct} onChange={(e) => setLine(idx, 'tax_pct', e.target.value)} data-testid={`line-tax-${idx}`} /></div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-xs">Billing</Label>
                <Select value={li.billing} onValueChange={(v) => setLine(idx, 'billing', v)}>
                  <SelectTrigger data-testid={`line-billing-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn"><Plus className="w-4 h-4 mr-1.5" /> Add line item</Button>
        </CardContent>
      </Card>

      {/* Payment + totals */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Payment</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setField('currency', v)}>
                <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['usd', 'eur', 'gbp', 'cad', 'aud'].map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment option</Label>
              <Select value={form.payment_option} onValueChange={(v) => setField('payment_option', v)}>
                <SelectTrigger data-testid="payment-option-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Pay in full</SelectItem>
                  <SelectItem value="deposit">Deposit</SelectItem>
                  <SelectItem value="recurring">Recurring</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payment_option === 'deposit' && (
              <div><Label>Deposit %</Label><Input type="number" min="1" max="100" value={form.deposit_pct} onChange={(e) => setField('deposit_pct', e.target.value)} data-testid="deposit-pct-input" /></div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Totals (preview)</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm" data-testid="totals-preview">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{money(totals.subtotal, form.currency)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Discount</span><span>-{money(totals.discount, form.currency)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span>{money(totals.tax, form.currency)}</span></div>
            <div className="flex justify-between font-semibold pt-2 border-t border-zinc-100"><span>One-time total</span><span data-testid="one-time-total">{money(totals.oneTime, form.currency)}</span></div>
            {totals.monthly > 0 && <div className="flex justify-between font-semibold text-indigo-700"><span>Monthly</span><span data-testid="monthly-total">{money(totals.monthly, form.currency)}/mo</span></div>}
          </CardContent>
        </Card>
      </div>

      {!isNew && !isDraft && (
        <Card data-testid="proposal-activity">
          <CardHeader><CardTitle className="text-base">Activity</CardTitle></CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-sm text-zinc-400">No activity yet.</p>
            ) : (
              <ul className="space-y-2">
                {events.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                    <div>
                      <span className="font-medium capitalize">{ev.type}</span>
                      <span className="text-zinc-400"> · {ev.actor}</span>
                      {ev.signature?.name && <span className="text-zinc-600"> — signed by {ev.signature.name}</span>}
                      {ev.meta?.body && <span className="text-zinc-600"> — “{ev.meta.body}”</span>}
                      {ev.meta?.reason && <span className="text-zinc-600"> — {ev.meta.reason}</span>}
                      <p className="text-xs text-zinc-400">{new Date(ev.created_at).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI draft dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent data-testid="ai-draft-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" /> Draft with AI</DialogTitle>
            <DialogDescription>Describe the project in a sentence or two. AI will write the intro, scope, terms, and suggest line items. You can edit everything after.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            placeholder="e.g. 3-month social media management for a coffee brand: 12 posts/mo, monthly reporting, and a one-time brand strategy workshop."
            value={aiBrief}
            onChange={(e) => setAiBrief(e.target.value)}
            data-testid="ai-brief-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button onClick={runAiDraft} disabled={aiBusy} data-testid="ai-generate-btn">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF preview modal — preview, then Save to desktop or Send to client */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="preview-pdf-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-indigo-600" /> Proposal preview</DialogTitle>
            <DialogDescription>This is exactly what your client sees and what the PDF contains.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 flex-1 bg-zinc-100">
            <div className="my-4 shadow-lg rounded-lg overflow-hidden bg-white">
              <ProposalDocument ref={docRef} snap={formToSnap()} />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 gap-2 sm:justify-between">
            <Button variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="preview-save-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />} Save to desktop
            </Button>
            <div className="flex items-center gap-2">
              {isNew ? (
                <span className="text-xs text-zinc-500">Create the draft first to send</span>
              ) : !form.prospect.email ? (
                <span className="text-xs text-zinc-500">Add a client email to send</span>
              ) : null}
              <Button
                onClick={sendFromPreview}
                disabled={sending || isNew || !form.prospect.email || !(form.status === 'draft' || form.status === 'sent' || form.status === 'viewed')}
                data-testid="preview-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {form.status === 'draft' ? 'Send to client' : 'Resend to client'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalBuilderPage;
export { ProposalBuilderPage };
